// src/components/BillImpact.jsx
// Two views over PASSED bills, for city tenants only:
//   1. Donut — of everything that passed in a category, what share went to each
//      purpose (funding, new units, regulation, ...). Pass RATE is not shown:
//      434 of 436 recorded 2026 votes passed, so a rate chart would read ~100%
//      across every slice and say nothing. Composition is the answerable question.
//   2. Schematic district map — how many passed bills name a place in each of
//      SF's 11 supervisorial districts.
//
// Both are keyword-derived from the vote record and approximate; the headers say
// so. Nothing here claims a bill was "good" — the vote record carries no such
// signal — so the map is labelled board activity / investment, not benefit.
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../utils/supabaseClient';
import { openInNewTab } from './billCategories';
import './BillImpact.css';

/* ── Impact purposes, checked in order (first match wins) ──
   'procedural' is checked FIRST: a continuance or committee referral names the
   same subjects as a substantive bill, so matching it last would misfile ~29%
   of passed votes as real policy action. */
const IMPACT_TYPES = [
  {
    id: 'procedural',
    label: 'Procedural',
    hint: 'Continuances, referrals, first readings, appointments, minutes — no direct policy effect',
    test: (t) =>
      /^(continue|re-refer|refer|table|excuse|suspend|rescind|adopt .{0,30}minutes|approve .{0,30}minutes)/.test(t) ||
      /\bfirst reading\b|\bconsent calendar\b|\bto committee\b|committee$/.test(t) ||
      /\b(re)?appointment(s)?\b|\bappoint\b/.test(t),
  },
  {
    id: 'funding',
    label: 'Funding & financing',
    hint: 'Revenue notes, loans, grants, bonds, trust-fund appropriations',
    test: (t) =>
      /revenue note|loan|grant|bond|appropriat|trust fund|financing|accept and expend|fund\b|budget|tax credit|subsid/.test(t),
  },
  {
    id: 'development',
    label: 'New units & projects',
    hint: 'Approving specific developments, ground leases, capital projects',
    test: (t) =>
      /ground lease|development|project|construct|rehab|infrastructure|acquisition|new .{0,20}(housing|units|facility)/.test(t),
  },
  {
    id: 'regulation',
    label: 'Regulation & zoning',
    hint: 'Zoning maps, code changes, permitting rules, standards, requirements',
    test: (t) =>
      /zoning|rezone|height.{0,10}bulk|code|ordinance|requirement|inclusionary|permit|conditional use|standard|regulat|inspection|planning/.test(t),
  },
  {
    id: 'protection',
    label: 'Resident protection',
    hint: 'Eviction and rent protections, tenant rights, anti-displacement',
    test: (t) => /evict|tenant|rent (relief|forgiveness|control|increase)|displac|protection|relocation/.test(t),
  },
  {
    id: 'services',
    label: 'Services & operations',
    hint: 'Shelter, health, safety and other direct service delivery or contracts',
    test: () => true, // catch-all
  },
];

const classifyImpact = (issue) => {
  const t = String(issue || '').toLowerCase();
  for (const type of IMPACT_TYPES) {
    if (type.test(t)) return type.id;
  }
  return 'services';
};

/* ── SF supervisorial districts (names only; geometry lives in the DB) ── */
const DISTRICTS = [
  { id: 1,  name: 'Richmond' },
  { id: 2,  name: 'Marina / Pac Heights' },
  { id: 3,  name: 'Chinatown / N. Beach' },
  { id: 4,  name: 'Sunset / Parkside' },
  { id: 5,  name: 'Tenderloin / W. Addition' },
  { id: 6,  name: 'SOMA / Mission Bay' },
  { id: 7,  name: 'West Portal / Ingleside' },
  { id: 8,  name: 'Castro / Noe Valley' },
  { id: 9,  name: 'Mission / Bernal' },
  { id: 10, name: 'Bayview / Potrero' },
  { id: 11, name: 'Excelsior / Outer Mission' },
];

/* Some bills only advocate on outside legislation. Place words inside a bill
   or act TITLE are not San Francisco locations — "Revitalizing Downtowns and
   Main Streets Act" is federal, and neither "Downtowns" nor "Main Streets"
   marks a spot on the map. Skip place matching entirely on these. */
const ADVOCACY_RE =
  /\b(support|supporting|oppose|opposing|urge|urging|endorse|endorsing)\b[\s\S]{0,80}\b(act|bill|h\.?\s?r\.?\s?\d|s\.?\s?b\.?\s?\d|a\.?\s?b\.?\s?\d|proposition|prop\.?\s?\d)\b/i;

/* ── Place → districts ──
   A place can span several districts. Neighborhoods usually sit inside one;
   named areas like "downtown" genuinely cross three. Every assignment was
   checked by point-in-polygon against the 2022 boundaries in
   district_boundaries, sampling the area (or, for corridors, points along it).

   Two classes of entry, and the difference matters:
   • LANDMARKS — neighborhoods, parks, named districts. Reliable: the name
     denotes one place.
   • STREETS — kept because bills cite numbered addresses on them, but a bare
     street name is a poor locator: Mission St crosses D6/D8/D9/D11 and Geary
     crosses D1/D2/D3/D5. Each is assigned the district its cited addresses
     actually fall in most often, which is a heuristic, not a fact.

   A bill naming no known place stays unplaced rather than guessed at. */
const PLACES = [
  /* ── Landmarks & neighborhoods ── */
  ['richmond', [1]], ['clement', [1]], ['lands end', [1]],
  ['marina', [2]], ['pacific heights', [2]], ['presidio', [2]], ['cow hollow', [2]],
  ['yacht harbor', [2]], ['upper fillmore', [2]], ['ghirardelli', [2]],
  ['chinatown', [3]], ['north beach', [3]], ['nob hill', [3]], ['fisherman', [3]],
  ['financial district', [3]], ['union square', [3]],
  ['sunset', [4]], ['parkside', [4]], ['ocean beach', [4]],
  ['western addition', [5]], ['hayes valley', [5]], ['japantown', [5]], ['haight', [5]],
  ['fillmore district', [5]], ['alamo square', [5]],
  // 2022 redistricting moved the Tenderloin and Civic Center into District 5.
  ['tenderloin', [5]], ['civic center', [5]], ['transgender district', [5]], ['mid-market', [5]],
  ['soma', [6]], ['south of market', [6]], ['treasure island', [6]], ['tida', [6]],
  ['mission bay', [6]], ['yerba buena', [6]], ['moscone', [6]],
  ['west portal', [7]], ['parkmerced', [7]], ['lakeside', [7]], ['stonestown', [7]],
  ['lake merced', [7]], ['twin peaks', [7]], ['balboa reservoir', [7]],
  ['castro', [8]], ['noe valley', [8]], ['glen park', [8]], ['duboce', [8]],
  ['diamond heights', [8]],
  ['mission district', [9]], ['bernal', [9]], ['portola', [9]],
  ['bayview', [10]], ['hunters point', [10]], ['dogpatch', [10]], ['candlestick', [10]],
  ['visitacion valley', [10]], ['pacific islander', [10]],
  ['excelsior', [11]], ['outer mission', [11]], ['ingleside', [11]], ['crocker', [11]],
  ['balboa park', [11]], ['oceanview', [11]],

  /* ── Areas that genuinely span districts (core points sampled) ── */
  // Plural "Downtowns" deliberately does not match: \b...\b stops at the "s",
  // which is what keeps the federal act title out of the map.
  [/\bdowntown\b/, [3, 5, 6]],
  ['golden gate park', [1, 4, 5]],

  /* ── Streets (heuristic — see note above) ── */
  ['geary', [1]],
  // Balboa Street is D1, but Balboa Reservoir (D7) and Balboa Park (D11) are
  // separate places that happen to share the name.
  [/\bbalboa\b(?!\s+(reservoir|park))/, [1]],
  ['union street', [2]], ['fillmore street', [2]],
  // Regex so a numbered address ("758 & 772 Pacific") matches while
  // "Pacific Heights" (D2) and "Pacific Islander" (D10) do not.
  [/\bpacific\b(?!\s+(heights|islander))/, [3]],
  ['jefferson street', [3]], ['columbus', [3]], ['stockton', [3]], ['jackson', [3]],
  ['judah', [4]], ['irving', [4]], ['taraval', [4]], ['great highway', [4]],
  ['divisadero', [5]], ['oak street', [5]], ['post street', [5]], ['turk', [5]],
  ['webster', [5]],
  ['market street', [6]], ['howard', [6]], ['folsom', [6]], ['bryant', [6]],
  ['harrison', [6]],
  ['ocean avenue', [7]], ['sloat', [7]],
  ['mission street', [9]], ['valencia', [9]], ['guerrero', [9]], ['cesar chavez', [9]],
  ['silver avenue', [9]],
  ['potrero', [10]], ['bayshore', [10]], ['third street', [10]], ['palou', [10]],
  ['evans', [10]], ['innes', [10]], ['jerrold', [10]],
  ['geneva', [11]], ['san jose avenue', [11]], ['alemany', [11]],
];

const districtsForIssue = (issue) => {
  const t = String(issue || '').toLowerCase();
  if (ADVOCACY_RE.test(t)) return [];
  const hits = new Set();
  for (const [matcher, districts] of PLACES) {
    const hit = matcher instanceof RegExp ? matcher.test(t) : t.includes(matcher);
    if (hit) districts.forEach((d) => hits.add(d));
  }
  return [...hits].sort((a, b) => a - b);
};

// Dollar figures stated in bill text ("$18,700,000", "$112M", "$3.75M").
const dollarsInIssue = (issue) => {
  const t = String(issue || '');
  let total = 0;
  const re = /\$\s?([\d,]+(?:\.\d+)?)\s?(million|billion|m\b|b\b)?/gi;
  let m;
  while ((m = re.exec(t)) !== null) {
    const n = parseFloat(m[1].replace(/,/g, ''));
    if (Number.isNaN(n)) continue;
    const unit = (m[2] || '').toLowerCase();
    if (unit.startsWith('b')) total += n * 1e9;
    else if (unit.startsWith('m')) total += n * 1e6;
    else total += n;
  }
  return total;
};

const fmtMoney = (n) => {
  if (!n) return null;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${Math.round(n / 1e6)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
};

// Ordinal blue ramp — slices are ordered by size, so magnitude (not identity)
// is the colour's job. Validated: monotone lightness, adjacent gaps, light end
// clears the surface.
const RAMP = ['#104281', '#1c5cab', '#2a78d6', '#5598e7', '#86b6ef', '#b7d3f6'];
// Sequential ramp for the map; index 0 means "no bills", so it stays near-surface.
const MAP_RAMP = ['#eef4fb', '#cde2fb', '#9ec5f4', '#5598e7', '#2a78d6', '#184f95'];

const polar = (cx, cy, r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

// Donut arc. A slice at exactly 100% can't be drawn as an arc (start and end
// coincide) — the caller renders a ring instead.
const arcPath = (cx, cy, rOuter, rInner, startDeg, endDeg) => {
  const [x1, y1] = polar(cx, cy, rOuter, startDeg);
  const [x2, y2] = polar(cx, cy, rOuter, endDeg);
  const [x3, y3] = polar(cx, cy, rInner, endDeg);
  const [x4, y4] = polar(cx, cy, rInner, startDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${x1} ${y1}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${x4} ${y4}`,
    'Z',
  ].join(' ');
};

/* ── Real geography ──
   Boundaries come from the `district_boundaries` table (simplified GeoJSON,
   loaded once from DataSF). Everything below converts lon/lat to SVG without a
   mapping library: at city scale an equirectangular projection with a cos(lat)
   width correction is visually indistinguishable from Mercator, and costs ~40
   lines instead of a dependency. */
const MAP_W = 300;
const MAP_H = 320;
const MAP_PAD = 8;

const ringsOf = (geometry) => {
  const polys = geometry && Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
  const out = [];
  for (const poly of polys) for (const ring of poly) if (ring && ring.length > 3) out.push(ring);
  return out;
};

const ringArea = (ring) => {
  let a = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    a += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
  }
  return Math.abs(a / 2);
};

const pointInRing = (x, y, ring) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

// Distance from a point to a ring's nearest edge — used to place labels away
// from boundaries rather than at a centroid, which can fall outside an L-shape.
const distToRing = (x, y, ring) => {
  let best = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [x1, y1] = ring[j];
    const [x2, y2] = ring[i];
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    const t = len2 ? Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / len2)) : 0;
    const d = Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy));
    if (d < best) best = d;
  }
  return best;
};

/* Build SVG paths + label anchors for every district, fitted to one shared
   viewBox so the districts line up into a single map of the city. */
function projectDistricts(rows) {
  let minx = 180;
  let miny = 90;
  let maxx = -180;
  let maxy = -90;
  for (const r of rows) {
    for (const ring of ringsOf(r.geometry)) {
      for (const [x, y] of ring) {
        if (x < minx) minx = x;
        if (x > maxx) maxx = x;
        if (y < miny) miny = y;
        if (y > maxy) maxy = y;
      }
    }
  }
  if (!(maxx > minx) || !(maxy > miny)) return null;

  // Longitude degrees shrink by cos(latitude); without this SF renders stretched.
  const k = Math.cos((((miny + maxy) / 2) * Math.PI) / 180);
  const w = (maxx - minx) * k;
  const h = maxy - miny;
  const s = Math.min((MAP_W - 2 * MAP_PAD) / w, (MAP_H - 2 * MAP_PAD) / h);
  const ox = (MAP_W - w * s) / 2;
  const oy = (MAP_H - h * s) / 2;
  const px = (x, y) => [ox + (x - minx) * k * s, oy + (maxy - y) * s];

  const out = new Map();
  for (const r of rows) {
    const rings = ringsOf(r.geometry);
    if (!rings.length) continue;

    let d = '';
    for (const ring of rings) {
      d += ring
        .map(([x, y], i) => {
          const [a, b] = px(x, y);
          return `${i ? 'L' : 'M'}${a.toFixed(1)} ${b.toFixed(1)}`;
        })
        .join('') + 'Z';
    }

    // Label goes in the largest part, at the interior point farthest from an edge.
    const main = rings.slice().sort((a, b) => ringArea(b) - ringArea(a))[0].map(([x, y]) => px(x, y));
    let bx = 0;
    let by = 0;
    let bd = -1;
    let lo = [Infinity, Infinity];
    let hi = [-Infinity, -Infinity];
    for (const [x, y] of main) {
      lo = [Math.min(lo[0], x), Math.min(lo[1], y)];
      hi = [Math.max(hi[0], x), Math.max(hi[1], y)];
    }
    const STEPS = 12;
    for (let i = 1; i < STEPS; i++) {
      for (let j = 1; j < STEPS; j++) {
        const x = lo[0] + ((hi[0] - lo[0]) * i) / STEPS;
        const y = lo[1] + ((hi[1] - lo[1]) * j) / STEPS;
        if (!pointInRing(x, y, main)) continue;
        const dist = distToRing(x, y, main);
        if (dist > bd) {
          bd = dist;
          bx = x;
          by = y;
        }
      }
    }
    if (bd < 0) {
      bx = (lo[0] + hi[0]) / 2;
      by = (lo[1] + hi[1]) / 2;
    }

    out.set(r.district_num, { d, lx: bx, ly: by, room: bd, supervisor: r.supervisor });
  }
  return out;
}

/* Schematic fallback — used only when a tenant has no boundary rows loaded.
   Relative positions only, not real boundaries. */
const DISTRICT_SHAPES = {
  1:  { x: 8,   y: 60,  w: 78,  h: 96 },
  2:  { x: 88,  y: 26,  w: 84,  h: 60 },
  3:  { x: 174, y: 26,  w: 78,  h: 60 },
  5:  { x: 88,  y: 88,  w: 84,  h: 68 },
  6:  { x: 174, y: 88,  w: 78,  h: 68 },
  4:  { x: 8,   y: 158, w: 78,  h: 74 },
  8:  { x: 88,  y: 158, w: 84,  h: 74 },
  9:  { x: 174, y: 158, w: 78,  h: 74 },
  7:  { x: 8,   y: 234, w: 78,  h: 64 },
  11: { x: 88,  y: 234, w: 84,  h: 64 },
  10: { x: 174, y: 234, w: 78,  h: 64 },
};

function BillImpact({ votings, categories, classifyCategory, districtId }) {
  const [catId, setCatId] = useState(null);

  // Donut slices and legend rows deep-link into the Bills Passed tab, at the
  // selected category's matching purpose subsection — opened in a new tab so
  // this Analysis view stays open in place.
  const goToBills = (impactId) => {
    if (!catId) return;
    openInNewTab(`${window.location.pathname}?tab=billspassed#bp-${catId}-${impactId}`);
  };

  // Clicking a district opens Bills Passed filtered to that district's bills
  // within the selected category — the same set the map counted.
  const goToDistrictBills = (districtNum) => {
    if (!catId) return;
    openInNewTab(
      `${window.location.pathname}?tab=billspassed&cat=${catId}&district=${districtNum}#bp-${catId}`
    );
  };
  const [geo, setGeo] = useState(null); // Map<district_num, {d, lx, ly}> | null

  /* Real boundaries, loaded once per district. Best-effort: if the table is
     empty for this tenant or the read fails, the schematic tiles still render. */
  useEffect(() => {
    if (!districtId) return undefined;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('district_boundaries')
        .select('district_num, district_name, supervisor, geometry')
        .eq('districtId', districtId)
        .order('district_num');
      if (cancelled) return;
      if (error || !data || !data.length) {
        setGeo(null);
        return;
      }
      setGeo(projectDistricts(data));
    })();
    return () => { cancelled = true; };
  }, [districtId]);

  /* Passed bills only, grouped by category. */
  const passed = useMemo(
    () =>
      (votings || []).filter((v) => {
        const m = String(v.outcome || '').match(/^(\d+)\s*-\s*(\d+)$/);
        return m && Number(m[1]) > Number(m[2]);
      }),
    [votings]
  );

  const byCategory = useMemo(() => {
    const map = new Map();
    for (const v of passed) {
      const id = classifyCategory(`${v.category || ''} ${v.issue || ''}`, v.category);
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(v);
    }
    return map;
  }, [passed, classifyCategory]);

  const available = useMemo(
    () => categories.filter((c) => (byCategory.get(c.id) || []).length > 0)
      .sort((a, b) => (byCategory.get(b.id).length - byCategory.get(a.id).length)),
    [categories, byCategory]
  );

  useEffect(() => {
    if (!catId && available.length) setCatId(available[0].id);
  }, [available, catId]);

  const slices = useMemo(() => {
    if (!catId) return null;
    const bills = byCategory.get(catId) || [];
    if (!bills.length) return null;
    const counts = new Map();
    for (const b of bills) {
      const id = classifyImpact(b.issue);
      const e = counts.get(id) || { n: 0, dollars: 0 };
      e.n += 1;
      e.dollars += dollarsInIssue(b.issue);
      counts.set(id, e);
    }
    const rows = IMPACT_TYPES
      .map((t) => ({ type: t, ...(counts.get(t.id) || { n: 0, dollars: 0 }) }))
      .filter((r) => r.n > 0)
      .sort((a, b) => b.n - a.n);
    const total = rows.reduce((s, r) => s + r.n, 0);
    let angle = 0;
    const withGeom = rows.map((r, i) => {
      const share = (r.n / total) * 100;
      const sweep = (r.n / total) * 360;
      const g = { ...r, share, start: angle, end: angle + sweep, color: RAMP[Math.min(i, RAMP.length - 1)] };
      angle += sweep;
      return g;
    });
    return { rows: withGeom, total };
  }, [catId, byCategory]);

  // Follows the category picker: the map answers "where did THIS category's
  // passed bills land", so it stays in step with the donut above it.
  const mapData = useMemo(() => {
    const counts = new Map(DISTRICTS.map((d) => [d.id, { n: 0, dollars: 0 }]));
    let placed = 0;
    let citywide = 0;
    let citywideDollars = 0;
    // Substantive bills only — a continuance names a place but moves nothing.
    const inCategory = catId ? byCategory.get(catId) || [] : [];
    const substantive = inCategory.filter((b) => classifyImpact(b.issue) !== 'procedural');
    for (const b of substantive) {
      const hits = districtsForIssue(b.issue);
      const money = dollarsInIssue(b.issue);
      // A bill naming no place isn't a bill that affected nowhere — it applies
      // across the city (or its location is simply absent from the title).
      // Counting those only in the total, and never on the map, is what made
      // districts with no named bill read as zero.
      if (!hits.length) {
        citywide += 1;
        citywideDollars += money;
        continue;
      }
      placed += 1;
      for (const id of hits) {
        const e = counts.get(id);
        e.n += 1;
        e.dollars += money;
      }
    }
    // The ramp encodes the district-specific count only. Shading by the total
    // would put every district within ~2% of every other (90 citywide vs 92)
    // and flatten the map into a single wash with no signal left in it.
    const max = Math.max(1, ...Array.from(counts.values()).map((e) => e.n));
    return {
      counts,
      max,
      placed,
      citywide,
      citywideDollars,
      substantiveTotal: substantive.length,
    };
  }, [catId, byCategory]);

  if (!slices || !available.length) {
    return (
      <div className="bi-card bi-empty">
        No passed bills to analyze yet for this district.
      </div>
    );
  }

  const activeCat = available.find((c) => c.id === catId);
  const cx = 130;
  const cy = 130;
  const only = slices.rows.length === 1;

  return (
    <>
      {/* ── Donut: composition of what passed ── */}
      <div className="bi-card">
        <div className="bi-head">
          <div>
            <h3 className="bi-title">What passed bills actually did</h3>
            <p className="bi-sub">
              Of the {slices.total} bill{slices.total === 1 ? '' : 's'} that passed in this
              category, the share directed at each purpose.
            </p>
          </div>
          <label className="bi-picker">
            <span>Category</span>
            <select value={catId || ''} onChange={(e) => setCatId(e.target.value)}>
              {available.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label} ({byCategory.get(c.id).length})
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="bi-donut-row">
          <svg viewBox="0 0 260 260" className="bi-donut" role="img"
               aria-label={`Passed bills in ${activeCat?.label} by purpose`}>
            {only ? (
              <circle
                cx={cx} cy={cy} r={92} fill="none"
                stroke={slices.rows[0].color} strokeWidth={44}
                className="bi-slice"
                onClick={() => goToBills(slices.rows[0].type.id)}
              >
                <title>{`${slices.rows[0].type.label}: ${slices.rows[0].n} bills — click to list them`}</title>
              </circle>
            ) : (
              slices.rows.map((r) => (
                <path
                  key={r.type.id}
                  d={arcPath(cx, cy, 114, 70, r.start, r.end)}
                  fill={r.color}
                  stroke="#ffffff"
                  strokeWidth="2"
                  className="bi-slice"
                  onClick={() => goToBills(r.type.id)}
                >
                  <title>{`${r.type.label}: ${r.n} bills (${r.share.toFixed(1)}%) — click to list them`}</title>
                </path>
              ))
            )}
            <text x={cx} y={cy - 6} className="bi-donut-num">{slices.total}</text>
            <text x={cx} y={cy + 14} className="bi-donut-lbl">bills passed</text>
          </svg>

          <ul className="bi-legend">
            {slices.rows.map((r) => (
              <li key={r.type.id}>
                <button
                  type="button"
                  className="bi-legend-btn"
                  title={`${r.type.hint} — click to list these bills`}
                  onClick={() => goToBills(r.type.id)}
                >
                  <span className="bi-sw" style={{ background: r.color }} />
                  <span className="bi-legend-label">{r.type.label}</span>
                  <span className="bi-legend-val">
                    {r.share.toFixed(1)}%
                    <span className="bi-legend-n">
                      {' '}· {r.n}
                      {r.dollars ? ` · ${fmtMoney(r.dollars)}` : ''}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <p className="bi-note">
          Purposes are keyword-derived from each bill&rsquo;s text and are approximate.
          Pass <em>rate</em> is deliberately not shown: nearly every recorded vote
          passes, so a rate chart would read close to 100% for every purpose.
          Dollar figures count only amounts stated in the bill title.
        </p>
      </div>

      {/* ── Schematic district map ── */}
      <div className="bi-card">
        <h3 className="bi-title">
          Where passed bills land — {activeCat?.label}
        </h3>
        <p className="bi-sub">
          Every district carries the {mapData.citywide} citywide{' '}
          <strong>{activeCat?.label}</strong> bill
          {mapData.citywide === 1 ? '' : 's'}; darker outlined districts also have
          bills naming them specifically. Follows the category selected above.
        </p>

        <div className="bi-baseline">
          <span className="bi-baseline-n">{mapData.citywide}</span>
          <span>
            of {mapData.substantiveTotal} substantive {activeCat?.label} bill
            {mapData.substantiveTotal === 1 ? '' : 's'} apply citywide or name no
            district — they count toward <strong>every</strong> district below.
            {mapData.placed > 0
              ? ` ${mapData.placed} name a specific district.`
              : ' None name a specific district, so no district is singled out.'}
          </span>
        </div>

        <div className="bi-map-row">
          <svg
            viewBox={geo ? `0 0 ${MAP_W} ${MAP_H}` : '0 0 260 306'}
            className="bi-map"
            role="img"
            aria-label="Passed bills by San Francisco supervisorial district"
          >
            {DISTRICTS.map((d) => {
              const e = mapData.counts.get(d.id);
              // Every district is affected by the citywide bills, so the number
              // shown is the total that applies here — never a bare dash, which
              // read as "nothing landed in this district".
              const total = mapData.citywide + e.n;
              // Baseline (step 1) for citywide-only districts: visibly filled,
              // not empty. Districts with their own bills climb the ramp.
              const step = e.n === 0
                ? 1
                : Math.min(MAP_RAMP.length - 1, 2 + Math.round((e.n / mapData.max) * (MAP_RAMP.length - 3)));
              const fill = MAP_RAMP[step];
              const shape = geo && geo.get(d.id);
              // Only districts with their OWN bills are clickable — clicking a
              // citywide-only district would open the same list 11 times.
              const clickable = e.n > 0;
              const tip =
                `District ${d.id} — ${d.name}` +
                (shape && shape.supervisor ? ` (${shape.supervisor})` : '') +
                `: ${total} passed bill${total === 1 ? '' : 's'} apply — ` +
                `${mapData.citywide} citywide` +
                (e.n > 0
                  ? `, ${e.n} naming this district${e.dollars ? ` (${fmtMoney(e.dollars)} stated)` : ''}` +
                    ' — click to list them'
                  : ', none naming it specifically');
              const clickProps = clickable
                ? {
                    className: 'bi-district-hit bi-district-own',
                    onClick: () => goToDistrictBills(d.id),
                    role: 'button',
                    tabIndex: 0,
                    onKeyDown: (ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        goToDistrictBills(d.id);
                      }
                    },
                    'aria-label': tip,
                  }
                : {};

              if (shape) {
                // Narrow districts (D5 is only ~9 units of interior room) can't
                // hold a stacked label — they get the number alone, with the
                // count still available on hover and in the ranked list.
                const dark = step >= 3 ? 'on-dark' : '';
                const roomy = shape.room > 11;
                return (
                  <g key={d.id} {...clickProps}>
                    <path
                      d={shape.d}
                      fill={fill}
                      stroke={e.n > 0 ? '#0f172a' : '#ffffff'}
                      strokeWidth={e.n > 0 ? 2 : 1.2}
                    >
                      <title>{tip}</title>
                    </path>
                    {roomy ? (
                      <>
                        <text x={shape.lx} y={shape.ly - 2} className={`bi-map-d ${dark}`}>
                          D{d.id}
                        </text>
                        <text x={shape.lx} y={shape.ly + 12} className={`bi-map-n ${dark}`}>
                          {total}
                          {e.n > 0 ? <tspan className="bi-map-own">{` +${e.n}`}</tspan> : null}
                        </text>
                      </>
                    ) : (
                      <text x={shape.lx} y={shape.ly + 4} className={`bi-map-d bi-map-d-sm ${dark}`}>
                        D{d.id}
                      </text>
                    )}
                  </g>
                );
              }

              // Schematic fallback (no boundary rows for this tenant).
              const r = DISTRICT_SHAPES[d.id];
              return (
                <g key={d.id} {...clickProps}>
                  <rect x={r.x} y={r.y} width={r.w} height={r.h}
                        rx="6" fill={fill}
                        stroke={e.n > 0 ? '#0f172a' : '#ffffff'}
                        strokeWidth={e.n > 0 ? 2.5 : 2}>
                    <title>{tip}</title>
                  </rect>
                  <text x={r.x + r.w / 2} y={r.y + r.h / 2 + 1}
                        className={`bi-map-d ${step >= 3 ? 'on-dark' : ''}`}>
                    D{d.id}
                  </text>
                  <text x={r.x + r.w / 2} y={r.y + r.h / 2 + 17}
                        className={`bi-map-n ${step >= 3 ? 'on-dark' : ''}`}>
                    {total}
                    {e.n > 0 ? <tspan className="bi-map-own">{` +${e.n}`}</tspan> : null}
                  </text>
                </g>
              );
            })}
          </svg>

          <div className="bi-rank-wrap">
            <p className="bi-rank-head">
              Applies everywhere
            </p>
            <button
              type="button"
              className="bi-rank-city"
              title={`Open all ${activeCat?.label} bills in Bills Passed`}
              onClick={() => catId && openInNewTab(
                `${window.location.pathname}?tab=billspassed#bp-${catId}`
              )}
            >
              <span className="bi-rank-name">All 11 districts · citywide</span>
              <span className="bi-rank-n">
                {mapData.citywide}
                {mapData.citywideDollars
                  ? <span className="bi-rank-$"> · {fmtMoney(mapData.citywideDollars)}</span>
                  : null}
              </span>
            </button>

            <p className="bi-rank-head">
              {mapData.placed > 0
                ? 'Additionally named in'
                : 'No district named specifically'}
            </p>
            <ol className="bi-rank">
              {DISTRICTS
                .map((d) => ({ d, ...mapData.counts.get(d.id) }))
                .filter((r) => r.n > 0)
                .sort((a, b) => b.n - a.n)
                .slice(0, 6)
                .map((r) => (
                  <li key={r.d.id}>
                    <button
                      type="button"
                      className="bi-rank-btn"
                      title={`List the ${r.n} ${activeCat?.label} bill${r.n === 1 ? '' : 's'} naming District ${r.d.id}`}
                      onClick={() => goToDistrictBills(r.d.id)}
                    >
                      <span className="bi-rank-d">D{r.d.id}</span>
                      <span className="bi-rank-name">{r.d.name}</span>
                      <span className="bi-rank-n">
                        +{r.n}
                        {r.dollars ? <span className="bi-rank-$"> · {fmtMoney(r.dollars)}</span> : null}
                      </span>
                    </button>
                  </li>
                ))}
            </ol>
          </div>
        </div>

        <p className="bi-note">
          {geo ? (
            <>
              Boundaries are the City&rsquo;s official supervisorial districts
              (DataSF, 2022 redistricting), simplified for display — accurate in
              shape and position, not to the street. The Farallon Islands, legally
              part of District 4, are omitted so the city fills the frame.
            </>
          ) : (
            <>
              <strong>Schematic, not a geographic map</strong> — tiles show districts
              in roughly their relative positions, not real boundaries.
            </>
          )}{' '}
          Shading counts board activity and investment, not benefit: the vote
          record contains no measure of whether a bill helped an area, so nothing
          here should be read as ranking outcomes. The number on each district is
          every substantive bill that applies there — the {mapData.citywide}{' '}
          citywide plus any naming it, shown as{' '}
          <span className="bi-map-own-inline">+N</span>. Shade depth and the dark
          outline track only the district-specific ones, since shading by the
          total would put all eleven within a couple of percent of each other and
          flatten the map. &ldquo;Citywide&rdquo; means the title names no
          recognizable place: most are genuinely citywide (appointments,
          procurement, grants), but a few are local with the address stated only
          in the full text. Procedural votes are excluded throughout, and a bill
          naming several districts counts once in each.
        </p>
      </div>
    </>
  );
}

export default BillImpact;
export { classifyImpact, districtsForIssue, dollarsInIssue, IMPACT_TYPES, DISTRICTS };
